import DialogContentText from '@mui/material/DialogContentText';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';

import { ReactComponent as WarningRoundedIcon } from 'assets/WarningRoundedIcon.svg';
import { Button } from 'common/ui/Button';
import Dialog from 'common/ui/Dialog';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import { Colors } from 'styles';
import { useFieldsDispatch } from '../Fields/Fields.Context';
import { useDiscardReport } from 'hooks/useDiscardReport';

import {
  defaultUnsignedReportWarningDialogState,
  discardReportState,
  hasReportContentChangedState,
  isReporterLockedState,
  reportStatusState,
  unsignedReportDialogState,
} from '../state';
import type { ReportStatuses } from '../state';
import { logger } from 'modules/logger';
import { useReportLockedState } from '../hooks/useReportLockedState/useReportLockedState';
import {
  isReportStatusEditable,
  useUnsignedReportWarningDialog,
} from './useUnsignedReportWarningDialog';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { OnSubmitParams } from '../Fields/types';
import { telemetryContextFor } from 'modules/Apollo/types';

const LoadingIndicator = ({
  'data-testid': dataTestID,
  label,
}: {
  ['data-testid']: string;
  label: string;
}): React.ReactElement => (
  <Stack space="small">
    <div
      css={`
        margin: 0 12px -5px -15px;
        padding: 2px 0 0 0;
      `}
    >
      <CircularProgress
        size="20px"
        style={{ color: label === 'Draft' ? Colors.white : Colors.gray10 }}
        data-testid={dataTestID}
      />
    </div>
    <Text color={Colors.gray7}>{label}</Text>
  </Stack>
);

export const UnsignedReportWarningDialog = ({
  submitting,
  onSubmit,
  isLinkCasesMutating = false,
}: {
  submitting: boolean;
  onSubmit: (params: OnSubmitParams) => Promise<void>;
  isLinkCasesMutating?: boolean;
}): React.ReactElement => {
  const { handleDiscardReport } = useDiscardReport({ context: APOLLO_QUERY_CONTEXT });
  const { handleSaveDraft, handleCancelAddAddendum } = useFieldsDispatch();
  const { onDialogActionSuccess, onDialogActionError } = useUnsignedReportWarningDialog();

  const [dialogState, setDialogState] = useRecoilState(unsignedReportDialogState);
  const discardReportOptions = useRecoilValue(discardReportState);
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);
  const isReporterLocked = useRecoilValue(isReporterLockedState);
  const hasReportContentChanged = useRecoilValue(hasReportContentChangedState);

  // The worklist tab evaluates shouldShowDialogWhenReadOrViewCase against its local Recoil state,
  // which is synced via BroadcastChannel. If reportStatus or isReporterLocked have changed since
  // the worklist made its decision — e.g. reportStatus moved to ProvisionalSubmit during the
  // signing countdown — the dialog was opened on stale data and should be dismissed.
  //
  // hasReportContentChanged must also be false: if content is genuinely still changed we let the
  // dialog show regardless of status, since suppressing it could discard real unsaved work.
  useEffect(() => {
    if (!dialogState.worklistActionQueued) {
      return;
    }
    const isDialogStale =
      !hasReportContentChanged && (isReporterLocked || !isReportStatusEditable(reportStatus));
    if (isDialogStale && !dialogState.readyForWorklistAction) {
      logger.info(
        '[UnsignedReportWarningDialog] Auto-dismissing stale dialog: reporter-side conditions no longer met',
        { reportStatus, isReporterLocked, hasReportContentChanged }
      );
      onDialogActionSuccess();
    }
  }, [
    dialogState.worklistActionQueued,
    dialogState.readyForWorklistAction,
    reportStatus,
    isReporterLocked,
    hasReportContentChanged,
    onDialogActionSuccess,
  ]);
  const { isAddendumStatus } = useReportLockedState({ context: APOLLO_QUERY_CONTEXT });
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const onCancel = useCallback(() => {
    // We DON'T want to close the window if the user cancels the dialog
    setDialogState(defaultUnsignedReportWarningDialogState);
  }, [setDialogState]);

  const onDiscard = useCallback(async () => {
    if (isAddendumStatus) {
      await handleCancelAddAddendum({
        onSuccess: onDialogActionSuccess,
        onError: onDialogActionError,
      });
    } else {
      await handleDiscardReport({ onSuccess: onDialogActionSuccess, onError: onDialogActionError });
    }
  }, [
    isAddendumStatus,
    handleCancelAddAddendum,
    onDialogActionSuccess,
    onDialogActionError,
    handleDiscardReport,
  ]);

  const onSign = useCallback(async () => {
    await onSubmit({ onSuccess: onDialogActionSuccess, onError: onDialogActionError });
  }, [onSubmit, onDialogActionSuccess, onDialogActionError]);

  const onDraft = useCallback(async () => {
    setIsSavingDraft(true);
    try {
      await handleSaveDraft({
        onSuccess: onDialogActionSuccess,
        onError: onDialogActionError,
      });
    } finally {
      setIsSavingDraft(false);
    }
  }, [handleSaveDraft, onDialogActionSuccess, onDialogActionError]);

  return (
    <UnsignedReportWarningDialogContent
      open={dialogState.open}
      onCancel={onCancel}
      onDiscard={onDiscard}
      onSign={onSign}
      onDraft={onDraft}
      isSavingDraft={isSavingDraft}
      isDiscarding={discardReportOptions.discarding}
      isSubmitting={submitting}
      isLinkCasesMutating={isLinkCasesMutating}
      isAddendumStatus={isAddendumStatus}
    />
  );
};

export const UnsignedReportWarningDialogContent = ({
  open,
  onCancel,
  onDiscard,
  onSign,
  onDraft,
  isSavingDraft,
  isDiscarding,
  isSubmitting,
  isLinkCasesMutating = false,
  isAddendumStatus,
}: {
  open: boolean;
  onCancel: () => void;
  onDiscard: () => Promise<void>;
  onSign: () => Promise<void>;
  onDraft: () => Promise<void>;
  isSavingDraft: boolean;
  isDiscarding: boolean;
  isSubmitting: boolean;
  isLinkCasesMutating?: boolean;
  isAddendumStatus: boolean;
}): React.ReactElement => {
  const [isReporterSubmissionEnabled] = useFeatureFlagEnabled(FF.ENABLE_REPORTER_SUBMISSION);

  const isActionInProgress = isSavingDraft || isDiscarding || isSubmitting || isLinkCasesMutating;
  const slug = 'unsigned-report-warning';

  const secondaryButtonCss = `
    white-space: nowrap;
  `;

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          sx: {
            padding: '45px 30px 32px 30px',
            width: 500,
            height: 268,
          },
        },
      }}
    >
      <DialogContent
        sx={{
          width: 440,
          textAlign: 'center',
          padding: 0,
        }}
        data-testid={`${slug}-modal-testid`}
      >
        <WarningRoundedIcon
          css={`
            color: ${Colors.red3};
            height: 60px;
            width: 60px;
            display: block;
            margin: 0 auto 16px;
          `}
        />
        <DialogContentText style={{ marginBottom: '0' }}>
          <Text>
            There are unsaved changes in the current {isAddendumStatus ? 'addendum' : 'report'}.
          </Text>
        </DialogContentText>
        <DialogContentText style={{ marginBottom: '0' }}>
          <Text>Do you want to save a draft?</Text>
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
          gap: 2,
          padding: 0,
        }}
      >
        <Button
          data-testid={`${slug}-cancel-button`}
          variant="ghost"
          disabled={isActionInProgress}
          onClick={() => {
            if (isActionInProgress) return;
            onCancel();
          }}
        >
          <Text css={secondaryButtonCss}>Cancel</Text>
        </Button>
        <Button
          data-testid={`${slug}-discard-button`}
          variant="ghost"
          disabled={isActionInProgress}
          onClick={() => {
            if (isActionInProgress) return;
            onDiscard();
          }}
          css="margin-left: 0 !important;"
        >
          {isDiscarding ? (
            <LoadingIndicator label="Discard" data-testid="discard-circular-progress" />
          ) : (
            <Text css={secondaryButtonCss}>Discard</Text>
          )}
        </Button>
        {isReporterSubmissionEnabled && (
          <Button
            data-testid={`${slug}-sign-button`}
            variant="ghost"
            disabled={isActionInProgress}
            onClick={() => {
              if (isActionInProgress) return;
              onSign();
            }}
            css="margin-left: 0 !important;"
          >
            {isSubmitting ? (
              <LoadingIndicator label="Sign" data-testid="sign-circular-progress" />
            ) : (
              <Text css={secondaryButtonCss}>Sign</Text>
            )}
          </Button>
        )}
        <Button
          data-testid={`${slug}-draft-button`}
          variant="primary"
          disabled={isActionInProgress}
          onClick={() => {
            if (isActionInProgress) return;
            onDraft();
          }}
          css="margin-left: 0 !important;"
        >
          {isSavingDraft ? (
            <LoadingIndicator label="Draft" data-testid="draft-circular-progress" />
          ) : (
            <Text>Draft</Text>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnsignedReportWarningDialog;

UnsignedReportWarningDialog.displayName = 'UnsignedReportWarningDialog';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(UnsignedReportWarningDialog);
